import { Injectable, inject } from '@angular/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import { ParameterParserService } from './parameter-parser.service';
import { ContextService } from './context.service';
import { DataService } from 'src/app/services/data.service';
import { EndpointService } from 'src/app/services/endpoint.service';
import { AppContext } from 'src/app/models/AppContext';
import { TranslateService } from '@ngx-translate/core';
import { ParserService } from '../services/parser.service';
import { ResourceType } from '../models/ResourceType';

@Injectable({
  providedIn: 'root',
})
export class QrPopulateService {
  private readonly contextService = inject(ContextService);
  private readonly dataService = inject(DataService);
  private readonly endpointService = inject(EndpointService);
  private readonly paramParser = inject(ParameterParserService);
  private readonly translate = inject(TranslateService);
  private readonly parser = inject(ParserService);

  async populate(
    qPackage: fhirR4.Bundle,
    appContext: AppContext,
  ): Promise<fhirR4.QuestionnaireResponse> {
    const params = await this.prepareForPopulate(qPackage, appContext);
    const payload = await this.dataService
      .post<
        fhirR4.QuestionnaireResponse | fhirR4.Parameters
      >(params, this.endpointService.getPopulateUrl())
      .catch((e) => {
        console.log(e);
        throw new Error(this.translate.instant('Error.Prepopulate'));
      });
    const qr = this.unwrapQuestionnaireResponse(payload);
    this.logQRErrors(qr);
    return qr;
  }

  /**
   * Per SDC and DaVinci DTR, $populate returns a Parameters wrapper containing
   * the QuestionnaireResponse. Older / non-conformant servers may return the bare
   * QuestionnaireResponse directly. Accepts either shape.
   */
  unwrapQuestionnaireResponse(
    payload: fhirR4.QuestionnaireResponse | fhirR4.Parameters,
  ): fhirR4.QuestionnaireResponse {
    if (payload?.resourceType === ResourceType.QuestionnaireResponse) {
      return payload as fhirR4.QuestionnaireResponse;
    }
    if (payload?.resourceType === ResourceType.Parameters) {
      const params = payload as fhirR4.Parameters;
      const named = new Set([
        'response',
        'return',
        'questionnaire-response-out',
      ]);
      const namedMatch = params.parameter?.find(
        (p) =>
          p.name &&
          named.has(p.name) &&
          p.resource?.resourceType === ResourceType.QuestionnaireResponse,
      );
      const anyMatch = params.parameter?.find(
        (p) => p.resource?.resourceType === ResourceType.QuestionnaireResponse,
      );
      const qrParam = namedMatch ?? anyMatch;
      if (qrParam?.resource) {
        return qrParam.resource as fhirR4.QuestionnaireResponse;
      }
    }
    throw new Error(
      `$populate response did not contain a QuestionnaireResponse (got ${payload?.resourceType})`,
    );
  }

  async prepareForPopulate(
    qPackage: fhirR4.Bundle,
    appContext: AppContext,
  ): Promise<fhirR4.Parameters> {
    const questionnaire = this.parser.getQuestionnaireFromPackage(qPackage);
    const cqfParams = this.paramParser.getCqfParams(questionnaire, qPackage);
    const coverageId = this.contextService.getCoverageId(appContext);
    const patient = this.contextService.getPatientReference(appContext);
    const state = await this.dataService.getSofState();
    return this.paramParser.createParameterForPopulate(
      questionnaire,
      appContext,
      cqfParams,
      coverageId,
      state,
      patient,
    );
  }

  async logQRErrors(payload: fhirR4.QuestionnaireResponse) {
    try {
      const errorParams =
        this.paramParser.createPopulateErrorLogParameters(payload);
      if (errorParams) {
        await this.dataService.post(errorParams, this.getLogEndpoint());
      }
    } catch (err) {
      console.log('Error calling $DTRLogQuestionnaireErrors.', err);
    }
  }

  getLogEndpoint(): string {
    return this.endpointService.getLogErrorUrl();
  }
}
